import { useEffect, useRef, useState } from "react";
import { InitialsBadge } from "./InitialsBadge";

const MENTION_RE = /(^|\s)@([^\s@]*)$/;

export function MentionText({ value, onChange, onSubmit, onCancel, onMention, staff = [], placeholder, testId, autoFocus }) {
  const ref = useRef(null);
  const [query, setQuery] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);

  const matches = query === null ? [] : staff.filter((u) => u.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  const open = matches.length > 0;

  const detect = (text, caret) => {
    const m = text.slice(0, caret).match(MENTION_RE);
    setQuery(m ? m[2] : null);
    setActive(0);
  };

  const pick = (u) => {
    const el = ref.current;
    const caret = el.selectionStart;
    const before = value.slice(0, caret).replace(MENTION_RE, (_, sp) => `${sp}@${u.name} `);
    const next = before + value.slice(caret);
    onChange(next);
    onMention?.(u);
    setQuery(null);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(before.length, before.length); });
  };

  const onKeyDown = (e) => {
    if (open) {
      if (e.key === "ArrowDown") { e.preventDefault(); return setActive((a) => (a + 1) % matches.length); }
      if (e.key === "ArrowUp") { e.preventDefault(); return setActive((a) => (a - 1 + matches.length) % matches.length); }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); return pick(matches[active]); }
      if (e.key === "Escape") { e.preventDefault(); return setQuery(null); }
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onSubmit(); }
    if (e.key === "Escape" && onCancel) onCancel();
  };

  return (
    <div className="relative">
      <textarea ref={ref} rows={1} value={value} autoFocus={autoFocus} placeholder={placeholder} data-testid={testId}
        onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        className="w-full resize-none bg-transparent border-0 p-0 shadow-none text-[14px] leading-relaxed text-slate-900 placeholder:text-slate-400 outline-none focus:outline-none focus:ring-0 focus:shadow-none focus-visible:shadow-none focus-visible:outline-none" />
      {open && (
        <div className="absolute left-0 bottom-full mb-2 w-64 rounded-xl border border-[#E5E7EB] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)] py-1 z-30" data-testid="mention-dropdown">
          <div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">Mention a teammate</div>
          {matches.map((u, i) => (
            <button key={u.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              onMouseEnter={() => setActive(i)} data-testid={`mention-option-${u.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${i === active ? "bg-[#EFF6FF]" : ""}`}>
              <InitialsBadge name={u.name} size={26} />
              <span className="flex-1 min-w-0 text-[13px] font-semibold text-slate-800 truncate">{u.name}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MentionHighlight({ text, mentions = [] }) {
  if (!mentions.length) return text;
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length).map((m) => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = text.split(new RegExp(`(@(?:${names.join("|")}))`, "g"));
  return parts.map((p, i) => (p.startsWith("@") && mentions.some((m) => `@${m.name}` === p)
    ? <span key={i} className="px-1 rounded bg-[#DBEAFE] text-[#1E5FEB] font-semibold" data-testid="note-mention-chip">{p}</span>
    : p));
}
